import { HttpParams } from '@angular/common/http';
import merge from 'lodash/merge';
import { ISetParamsAdapter } from './base-params';
import { PageParams, PageParamsLike } from './page-params';

interface SortParam {
  selector: string;
  desc?: boolean;
}

type FilterExprClause = [string, string, any] | any;
type FilterExprSubClause =
  | FilterExprClause
  | [string, FilterExprClause]
  | [FilterExprClause | string | FilterExprClause]
  | [FilterExprClause | string | FilterExprClause | string | FilterExprClause]
  | [
      | FilterExprClause
      | string
      | FilterExprClause
      | string
      | FilterExprClause
      | string
      | FilterExprClause
    ]
  | Array<FilterExprClause>;
type FilterExprSubSubClause =
  | FilterExprSubClause
  | [string, FilterExprSubClause]
  | [FilterExprSubClause | string | FilterExprSubClause]
  | [
      | FilterExprSubClause
      | string
      | FilterExprSubClause
      | string
      | FilterExprSubClause
    ]
  | [
      | FilterExprSubClause
      | string
      | FilterExprSubClause
      | string
      | FilterExprSubClause
      | string
      | FilterExprSubClause
    ]
  | Array<FilterExprSubClause>;
export type FilterExpr = FilterExprSubSubClause[];

export interface LoadOptionsParamsLike extends PageParamsLike {
  extras?: any;
  sort?: SortParam[];
  filter?: FilterExpr;
  searchOperation?: string;
  searchExpr?: any;
  searchValue?: any;

  sortOptional?: SortParam[];
  filterOptional?: FilterExpr;
  skipOptional?: number;
  takeOptional?: number;
  searchOperationOptional?: string;
  searchExprOptional?: any;
  searchValueOptional?: any;
}

export class LoadOptionsParams
  extends PageParams<LoadOptionsParams, LoadOptionsParamsLike>
  implements LoadOptionsParamsLike
{
  extras?: any;
  sort?: SortParam[];
  filter?: FilterExpr;
  searchOperation?: string;
  searchExpr?: any;
  searchValue?: any;

  filterOptional?: FilterExpr;
  sortOptional?: SortParam[];
  searchOperationOptional?: string;
  searchExprOptional?: any;
  searchValueOptional?: any;

  static adaptToQueryStrSort(sort: SortParam[]) {
    let result: string;
    if (Array.isArray(sort) && sort.length) {
      result = JSON.stringify(sort);
    } else {
      result = '';
    }
    return result;
  }

  static adaptToArcGisWhere(filter?: any) {
    let result: string;
    if (Array.isArray(filter) && filter.length) {
      if (Array.isArray(filter[0])) {
        result =
          '(' +
          filter
            .map((x) => {
              return this.adaptToArcGisWhere(x);
            })
            .join(' ') +
          ')';
      } else if (filter.length === 2 && filter[0] === '!') {
        result = 'not ' + this.adaptToArcGisWhere([filter[1]]);
      } else if (filter.length === 3) {
        result = this.stringfyFilterParamArcgis(filter as any);
      } else {
        result = this.parseParamStr(filter);
      }
    } else if (!filter?.length) {
      result = '1=1';
    } else {
      result = this.parseParamStr(filter);
    }
    return result;
  }

  static adaptToArcGisSort(sort: SortParam[]) {
    return sort
      .map((x) => {
        return x.selector + (x.desc ? ' desc' : '');
      })
      .join(',');
  }

  private static stringfyFilterParamArcgis(value: [string, string, any]) {
    let filterValue = value[2];
    const field = this.parseParamStr(value[0]);
    const filterOperator = value[1];
    let operator: string;

    let prefix = '';
    let suffix = '';

    switch (filterOperator.toLowerCase()) {
      case 'startswith':
        operator = 'like';
        suffix = '%';
        break;
      case 'endswith':
        operator = 'like';
        prefix = '%';
        break;
      case 'contains':
        operator = 'like';
        prefix = suffix = '%';
        break;
      case 'notcontains':
        operator = 'not like';
        prefix = suffix = '%';
        break;
      default:
        operator = filterOperator;
        break;
    }

    if (filterValue === null) {
      switch (operator) {
        case '=':
          operator = 'is';
          break;
        case '<>':
          operator = 'is not';
          break;
      }
    }

    if (Array.isArray(filterValue)) {
      filterValue =
        '(' +
        filterValue
          .map((x) => {
            let valueStr = this.parseParamStr(x);

            if (typeof x === 'string') {
              valueStr =
                LoadOptionsParams.STRING_SYMBOL +
                valueStr +
                LoadOptionsParams.STRING_SYMBOL;
            }

            return valueStr;
          })
          .join(',') +
        ')';
    } else if (typeof filterValue === 'string') {
      filterValue =
        LoadOptionsParams.STRING_SYMBOL +
        prefix +
        filterValue +
        suffix +
        LoadOptionsParams.STRING_SYMBOL;
    } else {
      filterValue = this.parseParamStr(filterValue);
    }

    return `(${field} ${operator} ${filterValue})`;
  }

  override apply(item: Partial<LoadOptionsParamsLike>) {
    super.apply(item);
    this.extras = item?.extras;
    this.sort = item?.sort?.slice();
    this.filter = item?.filter?.slice();
    this.searchOperation = item?.searchOperation;
    this.searchExpr = item?.searchExpr;
    this.searchValue = item?.searchValue;

    this.sortOptional = item?.sortOptional?.slice();
    this.filterOptional = item?.filterOptional?.slice();
    this.searchOperationOptional = item?.searchOperationOptional;
    this.searchExprOptional = item?.searchExprOptional;
    this.searchValueOptional = item?.searchValueOptional;
  }

  setExtras(extras: any) {
    this.extras = merge(this.extras || {}, extras || {});
    return this;
  }

  setPrependExtras(extras: any) {
    this.extras = merge(extras || {}, this.extras || {});
    return this;
  }

  appendFilterBetween(
    field: string,
    start: any,
    end: any,
    groupingExpr = 'and'
  ) {
    const betweenExpr: FilterExprSubSubClause[] = [
      [field, '>=', start],
      'and',
      [field, '<=', end],
    ];

    if (!this.filter || !this.filter.length) {
      this.filter = betweenExpr;
    } else {
      this.filter = [this.filter as any, groupingExpr, betweenExpr];
    }
    return this;
  }

  appendFilterMinDate(field: string, minDate: any, groupingExpr = 'and') {
    const minExpr: FilterExprSubSubClause[] = [[field, '>=', minDate]];

    if (!this.filter || !this.filter.length) {
      this.filter = minExpr;
    } else {
      this.filter = [this.filter as any, groupingExpr, minExpr];
    }
    return this;
  }

  appendFilterMaxDate(field: string, maxDate: any, groupingExpr = 'and') {
    const minExpr: FilterExprSubSubClause[] = [[field, '<=', maxDate]];

    if (!this.filter || !this.filter.length) {
      this.filter = minExpr;
    } else {
      this.filter = [this.filter as any, groupingExpr, minExpr];
    }
    return this;
  }

  appendFilter(filterExpr: FilterExprSubSubClause[], groupingExpr = 'and') {
    if (!this.filter || !this.filter.length) {
      this.filter = filterExpr;
    } else {
      this.filter = [this.filter as any, groupingExpr, filterExpr];
    }
    return this;
  }

  prependSort(...sort: SortParam[]) {
    if (!this.sort) {
      this.sort = [];
    }
    Array.prototype.push.apply(sort, this.sort);
    this.sort = sort;
    return this;
  }

  appendSort(...sort: SortParam[]) {
    if (!this.sort) {
      this.sort = [];
    }
    Array.prototype.push.apply(this.sort, sort);
    return this;
  }

  appendFilterOptional(
    filterExpr: FilterExprSubSubClause[],
    groupingExpr = 'and'
  ) {
    if (!this.filterOptional || !this.filterOptional.length) {
      this.filterOptional = filterExpr;
    } else {
      this.filterOptional = [
        this.filterOptional as any,
        groupingExpr,
        filterExpr,
      ];
    }
    return this;
  }

  prependSortOptional(...sort: SortParam[]) {
    if (!this.sortOptional) {
      this.sortOptional = [];
    }
    Array.prototype.push.apply(sort, this.sortOptional);
    this.sortOptional = sort;
    return this;
  }

  appendSortOptional(...sort: SortParam[]) {
    if (!this.sortOptional) {
      this.sortOptional = [];
    }
    Array.prototype.push.apply(this.sortOptional, sort);
    return this;
  }

  override toHttpParams() {
    let params = new HttpParams();

    this._handleParamsAdapter({
      has(key: string) {
        return params.has(key);
      },

      set(key: string, param: string) {
        params = params.set(key, param);
      },
    });

    return params;
  }

  override toBodyParams() {
    let params: any = {};

    this._handleParamsAdapter({
      has(key: string) {
        return !!params[key];
      },

      set(key: string, param: string) {
        params[key] = param;
      },
    });

    return params;
  }

  toWhereClauseStr() {
    const filter = this.resolveFilters();
    return LoadOptionsParams.adaptToArcGisWhere(filter);
  }

  resolveFilters() {
    let filter: FilterExpr;
    if (this.filter && this.filter.length) {
      filter = this.filter;
    } else {
      filter = this.filterOptional ?? [];
    }

    const searchFilter = this.generateSearchFilterOptionIfCan();
    if (searchFilter) {
      filter =
        filter?.length > 0 ? [filter, 'and', searchFilter] : searchFilter;
    }

    return filter;
  }

  resolveSorters() {
    let sort: SortParam[];
    if (this.sort && this.sort.length) {
      sort = this.sort;
    } else {
      sort = this.sortOptional ?? [];
    }

    return sort;
  }

  private resolveSearchExpr() {
    return this.searchExpr ?? this.searchExprOptional;
  }

  private resolveSearchOperation() {
    return this.searchOperation ?? this.searchOperationOptional;
  }

  private resolveSearchValue() {
    return this.searchValue ?? this.searchValueOptional;
  }

  private generateSearchFilterOptionIfCan() {
    const searchExpr = this.resolveSearchExpr();
    const searchOperation = this.resolveSearchOperation();
    const searchValue = this.resolveSearchValue();
    if (searchExpr && searchOperation && searchValue !== undefined) {
      if (Array.isArray(searchExpr)) {
        const filter: any[] = [];
        for (let i = 0; i < searchExpr.length; ++i) {
          if (i > 0) {
            filter.push('or');
          }

          filter.push([searchExpr[i], searchOperation, searchValue]);
        }

        return filter.length ? filter : undefined;
      } else {
        return [searchExpr, searchOperation, searchValue];
      }
    }
    return undefined;
  }

  protected override _handleParamsAdapter(paramsAdapter: ISetParamsAdapter) {
    super._handleParamsAdapter(paramsAdapter);

    this.adaptToArcGis(paramsAdapter);

    if (this.extras) {
      const keys = Object.keys(this.extras);
      for (const key of keys) {
        paramsAdapter.set(
          key,
          LoadOptionsParams.parseParamStr(this.extras[key])
        );
      }
    }
  }

  private adaptToArcGis(paramsAdapter: ISetParamsAdapter) {
    const filter = this.resolveFilters();
    if (filter && filter.length) {
      const whereClause = LoadOptionsParams.adaptToArcGisWhere(filter);
      paramsAdapter.set('where', whereClause);
    } else {
      paramsAdapter.set('where', '1=1');
    }

    const sort = this.resolveSorters();
    if (sort && sort.length) {
      const sortParam = LoadOptionsParams.adaptToArcGisSort(sort);
      paramsAdapter.set('orderByFields', sortParam);
    }

    if (!paramsAdapter.has('outFields')) {
      paramsAdapter.set('outFields', '*');
    }
    if (!paramsAdapter.has('f')) {
      paramsAdapter.set('f', 'json');
    }
  }
}
